import { useMsal } from '@azure/msal-react';
import {
  ChatBubbleOutlineRounded,
  DarkModeOutlined,
  LightModeRounded,
  MenuRounded
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RRLink, useHistory } from 'react-router-dom';
import { ColorModeContext } from '../..';
import { RootState } from '../../app/store';
import { convertDateTime } from '../../functions/convertDateTime';
import { useAppDispatch } from '../../hook/useAppRudux';
import KeyIcon from '../../icon/KeyIcon';
import { signOut } from '../../pages/Auth/authSCG';
import ProfileIcon from '../ProfileIcon';

interface NavbarProps {
  handleDrawer: (
    open: boolean
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void;
  isLoggedIn: boolean;
}

export default function Navbar({ handleDrawer, isLoggedIn }: NavbarProps) {
  const dispatch = useAppDispatch();
  const { location } = useHistory();
  const colorMode = useContext(ColorModeContext);
  const { instance } = useMsal();

  // Add condition for replacing URL when navigating to other page
  const replace = location.pathname.startsWith('/change-passwords');

  const { exp, user } = useSelector((state: RootState) => state.userProfile);
  const isExternalUser = Boolean(user?.isExternalUser);

  const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorMenu);

  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('xs')
  );

  function handleClick(event: React.MouseEvent<HTMLButtonElement>): void {
    setAnchorMenu(event.currentTarget);
  }

  function handleClose(): void {
    setAnchorMenu(null);
  }

  function handleLogout() {
    handleClose();
    signOut({ dispatch, instance });
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        sx={(theme) => ({
          zIndex: { xs: theme.zIndex.appBar, lg: theme.zIndex.drawer + 1 },
          bgcolor: alpha(theme.palette.background.paper, 0.7),
          color: theme.palette.text.primary,
          backdropFilter: 'blur(8px)'
        })}
      >
        <Toolbar sx={{ display: 'flex' }}>
          {isLoggedIn && (
            <Box
              sx={{
                display: { xs: 'block', lg: 'none' },
                mr: { xs: -1.5, sm: 1 },
                ml: { xs: 1.5, sm: -0.5 },
                order: { xs: 1, sm: 0 }
              }}
            >
              <Tooltip title="Menu">
                <IconButton
                  size="large"
                  edge={isSmallScreen ? 'end' : 'start'}
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawer(true)}
                >
                  <MenuRounded />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box sx={{ ml: { xs: 0.5, sm: 0 }, flexGrow: 1 }}>
            <Typography
              component={RRLink}
              to="/"
              sx={(theme) => ({
                fontSize: { xs: '1.6rem', sm: '1.9rem' },
                letterSpacing: { xs: -1.5, sm: -2 },
                fontFamily: 'Comfortaa',
                fontWeight: 700,
                cursor: 'pointer',
                color: theme.palette.text.primary,
                textDecoration: 'none'
              })}
            >
              backspace
            </Typography>
          </Box>
          <Tooltip
            title={
              colorMode.mode === 'light'
                ? 'Turn off the light'
                : 'Turn on the light'
            }
          >
            <IconButton
              onClick={() => colorMode.toggleColorMode()}
              sx={(theme) => ({ color: theme.palette.text.primary })}
            >
              {colorMode.mode === 'light' ? (
                <DarkModeOutlined />
              ) : (
                <LightModeRounded />
              )}
            </IconButton>
          </Tooltip>
          {isLoggedIn ? (
            <ProfileIcon
              name={user ? `${user.firstName} ${user.lastName}` : undefined}
              size={40}
              onClick={handleClick}
              sx={{ fontSize: '1.25rem' }}
            />
          ) : (
            <Button
              component={RRLink}
              to="/login"
              variant="contained"
              color="primary"
              disableElevation
              sx={{ ml: 0.5 }}
            >
              Log in
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Menu
        id="menu"
        anchorEl={anchorMenu}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {isExternalUser && (
          <MenuItem
            component={RRLink}
            to="/change-passwords"
            replace={replace}
            onClick={handleClose}
          >
            <ListItemIcon>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change password</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            window.open(
              `${process.env.REACT_APP_ENC_PLATFORM_SUPPORT}`,
              '_blank'
            );
            handleClose();
          }}
        >
          <ListItemIcon>
            <ChatBubbleOutlineRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Contact backspace support</ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main', justifyContent: 'flex-end' }}
          onClick={handleLogout}
        >
          Log out
        </MenuItem>
        <Box sx={{ px: 2, textAlign: 'right' }}>
          <Typography component="p" variant="caption" color="textSecondary">
            your session will expire at
          </Typography>
          <Typography component="i" variant="body2">
            {convertDateTime(exp)}
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
}
